import { useCallback, useMemo, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import { OneLineCellStyles } from "@/controls/ag-grid/OneLineCells";
import type { Barrel } from "../models/Barrel";
import { useTableData } from "../hooks/useTableData";

interface BarrelsTableProps {
	barrels: Barrel[];
	selectedBarrel: Barrel | null;
	onRowSelected: (barrel: Barrel | null) => void;
	onDelete: (barrel: Barrel) => void;
	onToggleActive: (barrel: Barrel, active: boolean) => void;
}

export const BarrelsTable = ({
	barrels,
	selectedBarrel,
	onRowSelected,
	onDelete,
	onToggleActive,
}: BarrelsTableProps) => {
	const gridRef = useRef<AgGridReact<Barrel>>(null);
	const { columnDefs } = useTableData({ onDelete, onToggleActive });

	// One line per barrel: the global default grows a row to fit the delete button.
	const defaultColDef = useMemo(
		() => ({ ...globalDefaultColDef, wrapText: false, autoHeight: false }),
		[],
	);

	const onGridReady = useCallback((_params: GridReadyEvent<Barrel>) => {}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<Barrel>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<Barrel>(onRowSelected);

	const getRowId = useCallback((params: { data: Barrel }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<Barrel>
				ref={gridRef}
				rowData={barrels}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				rowHeight={28}
				headerHeight={32}
				enableBrowserTooltips
				getRowStyle={(params) => {
					if (selectedBarrel?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
